import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSupabaseClient } from '../lib/supabase';
import { getEnvVar } from '../lib/env';
import { apiPost, apiGet } from '../lib/api';
import { useAuth } from './useAuth';
import type { Webinar, UserWebinarRegistration } from '../types';

export function useWebinars() {
  const supabase = getSupabaseClient();

  return useQuery({
    queryKey: ['webinars'],
    queryFn: async () => {
      const now = new Date().toISOString();

      const { data, error } = await supabase
        .from('webinars')
        .select('*')
        .eq('is_published', true)
        .lte('showable_date', now)
        .or(`valid_until.is.null,valid_until.gte.${now}`) // Not expired
        .order('start_date', { ascending: true });

      if (error) throw error;
      return data as Webinar[];
    },
    refetchInterval: 60000, // Refresh every minute for countdown updates
  });
}

export function useWebinar(webinarId: string) {
  const supabase = getSupabaseClient();

  return useQuery({
    queryKey: ['webinar', webinarId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webinars')
        .select('*')
        .eq('id', webinarId)
        .single();

      if (error) throw error;
      return data as Webinar;
    },
    enabled: !!webinarId,
  });
}

export function useWebinarRegistrations() {
  const supabase = getSupabaseClient();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['webinar-registrations'],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('user_webinar_registrations')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      return data as UserWebinarRegistration[];
    },
    enabled: !!user,
    staleTime: 60 * 1000, // Cache for 1 minute
  });
}

export function useWebinarRegistration(webinarId: string | undefined) {
  const supabase = getSupabaseClient();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['webinar-registration', webinarId],
    queryFn: async () => {
      if (!webinarId || !user) return null;

      const { data, error } = await supabase
        .from('user_webinar_registrations')
        .select('*')
        .eq('user_id', user.id)
        .eq('webinar_id', webinarId)
        .maybeSingle();

      if (error) {
        throw error;
      }

      return data as UserWebinarRegistration | null;
    },
    enabled: !!webinarId && !!user,
  });
}

interface WebinarAccessResponse {
  hasAccess: boolean;
  canJoin: boolean;
  reason: string;
  hasStarted?: boolean;
  hasEnded?: boolean;
}

export function useWebinarAccess(webinarId: string | undefined) {
  const apiUrl = getEnvVar('VITE_API_URL');

  return useQuery({
    queryKey: ['webinar-access', webinarId],
    queryFn: async () => {
      if (!webinarId) return null;

      const response = await apiPost<WebinarAccessResponse>(
        `${apiUrl}/api/webinar/check-access`,
        { webinar_id: webinarId },
        true
      );

      return response;
    },
    enabled: !!webinarId,
    staleTime: 30 * 1000, // Cache for 30 seconds
  });
}

export function useRegisterForWebinar() {
  const queryClient = useQueryClient();
  const apiUrl = getEnvVar('VITE_API_URL');

  return useMutation({
    mutationFn: async (webinarId: string) => {
      const response = await apiPost<{ success: boolean; message: string }>(
        `${apiUrl}/api/webinar/register`,
        { webinar_id: webinarId },
        true
      );

      return response;
    },
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['webinar-registrations'] });
      queryClient.invalidateQueries({ queryKey: ['webinar-registration'] });
      queryClient.invalidateQueries({ queryKey: ['webinar-access'] });
    },
  });
}

export function useWebinarRoomUrl(webinarId: string | undefined) {
  const apiUrl = getEnvVar('VITE_API_URL');

  return useQuery({
    queryKey: ['webinar-room-url', webinarId],
    queryFn: async () => {
      if (!webinarId) return null;

      const response = await apiGet<{ roomUrl: string }>(
        `${apiUrl}/api/webinar/room-url?webinar_id=${webinarId}`,
        true
      );

      return response.roomUrl;
    },
    enabled: false, // Don't auto-fetch, call manually when user clicks "Join"
    staleTime: 0, // Always fetch fresh token
  });
}

// Get Stripe checkout URL for paid webinars
export async function getWebinarCheckoutUrl(webinarId: string): Promise<string> {
  const supabase = getSupabaseClient();
  const token = await supabase.auth.getSession();
  const apiUrl = getEnvVar('VITE_API_URL');

  const params = new URLSearchParams({
    return_url: window.location.origin + '/courses',
    webinar_id: webinarId,
  });

  const response = await fetch(`${apiUrl}/api/stripe/customer-portal?${params}`, {
    headers: {
      'Authorization': `Bearer ${token.data.session?.access_token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to get checkout URL');
  }

  const data = await response.json();
  return data.url;
}

// Get webinar price with discount info
export function useWebinarPrice(webinarId: string | undefined) {
  const apiUrl = getEnvVar('VITE_API_URL');
  const supabase = getSupabaseClient();

  return useQuery({
    queryKey: ['webinar-price', webinarId],
    queryFn: async () => {
      if (!webinarId) return null;

      // Get webinar to check if it has a price
      const { data: webinar } = await supabase
        .from('webinars')
        .select('price_id')
        .eq('id', webinarId)
        .single();

      if (!webinar?.price_id) {
        return null; // Free webinar
      }

      // Fetch price from API (includes discount info)
      const response = await fetch(
        `${apiUrl}/api/course/price?price_id=${webinar.price_id}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch webinar price');
      }

      const data = await response.json();
      return data;
    },
    enabled: !!webinarId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}
